<template>
  <div
    v-if="loading"
    class="installment-list"
  >
    <div class="animation-container">
      <div class="loading-animation">
        <DotLottieVue
          style="height: 150px; width: 150px; padding: 0"
          autoplay
          loop
          src="/assets/loader.lottie"
          :auto-resize-canvas="true"
        />
        <h4>{{ $t("Cargando...") }}</h4>
      </div>
    </div>
  </div>
  <div v-if="superPacmanInstallment && !error && !showNoPayments">
    <SuperPacmanInstallmentList
      :installment-details="superPacmanInstallment"
      @select-installment-item="handleSelectInstallment"
      @other-amount="handleOtherAmount"
    />
  </div>
  <div v-else-if="showNoPayments">
    <NoPayments :country-prop="country" />
  </div>
  <div v-else-if="error">
    <ErrorMessage :country-prop="country"/>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import NoPayments from "@/components/NoPayments.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import {useSuperPacmanInstallments} from "@/services/DataService";
import {
  ISuperPacmanFetchInstallmentRequest,
  ISuperPacmanInstallment,
  ISuperPacmanInstallmentItem
} from "@/types/ISuperPacman";
import SuperPacmanInstallmentList from "@/components/SuperPacmanInstallmentList.vue";

export default defineComponent({
  name: "SuperPacmanInstallmentsView",
  components: {
    DotLottieVue,
    SuperPacmanInstallmentList,
    NoPayments,
    ErrorMessage,
  },
  emits: ["select-installment-item", "other-amount"],
  setup() {
    const {t, n, locale} = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const deviceTag = computed(() => route.params.deviceTag);
    if (deviceTag.value) {
      store.dispatch("payOptions/setDeviceTag", deviceTag.value);
    }
    // Get the parameter "showoptions" from the URL, if it exists
    // otherwise, set it to option_a
    const showOptions = computed(() => {
      const option = route.query.showoptions ?? "option_a";
      if (option === "2" || option === "option_b") {
        return "option_b";
      } else {
        return "option_a";
      }
    });
    const forceUpdate = computed(() => {
      // Check if the parameter "forceUpdate" is present in the URL
      // If it is, return true, otherwise, return false.
      // This variable is used to force the update of the installments
      return route.query.forceUpdate === "true";
    });
    store.dispatch("payOptions/setPaymentOption", showOptions.value);
    store.dispatch("payOptions/setCurrencySymbol", n(0, "currency", locale.value).slice(0,1))
    const request: ISuperPacmanFetchInstallmentRequest = {
      deviceTag: deviceTag.value as string,
      optionName: showOptions.value as string
    };
    const {superPacmanInstallment, error} = useSuperPacmanInstallments(request);
    const loading = computed(() => !superPacmanInstallment.value);
    const country = computed(() => {
      return superPacmanInstallment.value?.financeProduct.country
    })
    const showNoPayments = computed(() => {
      let showNoPayments = false
      // USE FULL PAYOFF AMOUNT
      const noDaysToAmount = superPacmanInstallment.value?.daysToAmount.length === 0 ? true : false
      const fullPayoffEqualsZero = Number(superPacmanInstallment.value?.financeProduct.fullPayoffAmount) === 0 ? true : false

      if (noDaysToAmount && fullPayoffEqualsZero) {
        showNoPayments = true
      }
      return showNoPayments
    });
    return {
      t,
      superPacmanInstallment,
      router,
      route,
      deviceTag,
      store,
      loading,
      error,
      forceUpdate,
      country,
      showNoPayments
    };
  },
  watch: {
    superPacmanInstallment(newValue) {
      this.store.dispatch("payOptions/setCountry", newValue.financeProduct.country)
      this.store.dispatch("payOptions/setSuperPacmanInstallment", newValue as ISuperPacmanInstallment)
    }
  },
  methods: {
    handleSelectInstallment(selectedInstallmentItem: ISuperPacmanInstallmentItem) {
      this.$emit("select-installment-item", selectedInstallmentItem);
      this.router.push({name: "ProvidersByDeviceTagAndCountry", params: {usingSP: "true"}});
    },
    handleOtherAmount() {
      this.router.push({name: "OtherAmountView"})
    }
  },
});
</script>

<style scoped>
.installment-list {
  padding: 16px 22px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.animation-container {
  justify-content: center;
  align-items: center;
  flex: 1;
  display: flex;
}

.animation-container h4 {
  color: var(--Typography-Text-1);
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  margin: auto;
  margin-bottom: 80px;
}

.loading-animation {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
}
</style>
